import {AspectRatio, IconButton, Box, Text, useSlotRecipe} from '@chakra-ui/react3'
import PropTypes from 'prop-types'
import React, {useMemo, useState} from 'react'
import CTA from '../cta'
import {hasValidImageSrc, hasValidString, isVideoAsset} from '../../../utils/utils'
import teaserSlotRecipe from './index.recipe'
import {ArrowReloadHorizontalIcon} from '../../icons'
import {FlipAnimation} from '../flip-animation'
import Asset from '../asset'
import {useIntl} from 'react-intl'

/**
 * Renders a teaser component with media, text content, and optional call-to-action.
 * Intended for use within the Leap components library.
 */

const Teaser = ({
    asset,
    assetFlip,
    headline,
    copy,
    cta,
    aspectRatio,
    loading = false,
    widths,
    teaserBackground,
    variant = 'card'
}) => {
    const {formatMessage} = useIntl()
    const [flipped, setFlipped] = useState(false)

    const recipe = useSlotRecipe({recipe: teaserSlotRecipe})
    const styles = useMemo(() => recipe({teaserBackground}), [recipe, teaserBackground])

    const hasCopy = hasValidString(copy.text)
    const hasHeadline = hasValidString(headline)
    const hasCtaLabel = hasValidString(cta?.label)

    const videoRef = React.useRef(null)

    const front = (
        <Asset
            asset={asset}
            videoRef={videoRef}
            loading={loading}
            objectFit="cover"
            widths={widths}
        />
    )

    return (
        <>
            {variant === 'card' && (
                <Box css={styles.root}>
                    {!!asset && (
                        <AspectRatio ratio={aspectRatio} pb="spacing-24">
                            <Box css={styles.imageContainer}>
                                {hasValidImageSrc(assetFlip) ? (
                                    <FlipAnimation
                                        isFlipped={flipped}
                                        front={front}
                                        back={
                                            <Asset
                                                asset={assetFlip}
                                                objectFit="cover"
                                                widths={widths}
                                                loading="lazy" // always lazy as it's not initially visible
                                            />
                                        }
                                    />
                                ) : (
                                    front
                                )}

                                {hasValidImageSrc(assetFlip) && !isVideoAsset(asset) && (
                                    <IconButton
                                        css={styles.iconButton}
                                        variant="secondaryGhost"
                                        size="sm"
                                        aria-label={formatMessage({
                                            id: 'global.image.flip',
                                            defaultMessage: 'flip image'
                                        })}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            setFlipped(!flipped)
                                        }}
                                    >
                                        <ArrowReloadHorizontalIcon />
                                    </IconButton>
                                )}

                                {(hasValidImageSrc(assetFlip) || isVideoAsset(asset)) && (
                                    <Box
                                        position="absolute"
                                        bottom="0"
                                        left="0"
                                        right="0"
                                        height="10.219rem"
                                        bg="gradient.bottom.darken-40"
                                    ></Box>
                                )}
                            </Box>
                        </AspectRatio>
                    )}

                    <Box css={styles.container}>
                        <Box css={styles.textContainer}>
                            {hasHeadline && <Text css={styles.headline}>{headline}</Text>}
                            {hasCopy && <Text css={styles.copy}>{copy.text}</Text>}
                        </Box>
                        {hasCtaLabel && <CTA {...cta} />}
                    </Box>
                </Box>
            )}
        </>
    )
}

Teaser.propTypes = {
    asset: PropTypes.object.isRequired,
    assetFlip: PropTypes.object,
    aspectRatio: PropTypes.number,
    headline: PropTypes.string.isRequired,
    copy: PropTypes.object,
    cta: PropTypes.object,
    headingLevel: PropTypes.number,
    loading: PropTypes.bool,
    widths: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    teaserBackground: PropTypes.bool,
    variant: PropTypes.string
}

export default Teaser
